import {
  Camera,
  ChefHat,
  ClipboardList,
  LogOut,
  Plus,
  Search,
  Share2,
  Soup,
  Star,
  Trophy,
  UsersRound
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  addReview,
  addCategory,
  clearSession,
  createDish,
  createHousehold,
  deleteCategory,
  deleteDish,
  getCategories,
  getDishes,
  getStats,
  joinHousehold,
  loadSession,
  orderMeal,
  saveSession
} from "./api";
import { compressImage } from "./image";
import type { Category, Dish, DishFilters, Session, Stats } from "./types";

type Tab = "order" | "library" | "add" | "reviews" | "stats" | "family";

const fallbackCategories: Category[] = [
  "家常菜",
  "汤羹",
  "主食",
  "凉菜",
  "甜点",
  "早餐",
  "其他"
].map((name) => ({ id: name, name }));

function Stars({ value, onChange }: { value: number; onChange?: (value: number) => void }) {
  return (
    <div className="stars" aria-label={`${value} 星`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          className={star <= value ? "star is-on" : "star"}
          disabled={!onChange}
          key={star}
          onClick={() => onChange?.(star)}
          title={`${star} 星`}
          type="button"
        >
          <Star size={18} fill="currentColor" />
        </button>
      ))}
    </div>
  );
}

function AuthScreen({ onLogin }: { onLogin: (session: Session) => void }) {
  const [mode, setMode] = useState<"create" | "join">("create");
  const [householdName, setHouseholdName] = useState("今天吃什么");
  const [nickname, setNickname] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const session =
        mode === "create"
          ? await createHousehold(householdName.trim(), nickname.trim())
          : await joinHousehold(inviteCode.trim(), nickname.trim());
      saveSession(session);
      onLogin(session);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-panel">
        <div className="brand-mark">
          <ChefHat size={34} />
        </div>
        <h1>家里点菜</h1>
        <p>把拿手菜、照片、点菜和家人评分都收进一个手机菜单。</p>

        <div className="segmented">
          <button className={mode === "create" ? "is-active" : ""} onClick={() => setMode("create")} type="button">
            创建家庭
          </button>
          <button className={mode === "join" ? "is-active" : ""} onClick={() => setMode("join")} type="button">
            邀请码加入
          </button>
        </div>

        <form className="form-grid" onSubmit={submit}>
          {mode === "create" ? (
            <label>
              家庭菜单名
              <input
                onChange={(event) => setHouseholdName(event.target.value)}
                placeholder="例如：王家厨房"
                required
                value={householdName}
              />
            </label>
          ) : (
            <label>
              邀请码
              <input
                autoCapitalize="characters"
                onChange={(event) => setInviteCode(event.target.value)}
                placeholder="输入家人给你的邀请码"
                required
                value={inviteCode}
              />
            </label>
          )}
          <label>
            你的昵称
            <input
              onChange={(event) => setNickname(event.target.value)}
              placeholder="例如：老公"
              required
              value={nickname}
            />
          </label>
          {error && <p className="error-text">{error}</p>}
          <button className="primary-action" disabled={loading} type="submit">
            {loading ? "处理中..." : mode === "create" ? "创建菜单" : "加入菜单"}
          </button>
        </form>
      </section>
    </main>
  );
}

function DishCard({
  dish,
  compact,
  onOrder,
  onCook,
  onReview,
  onDelete
}: {
  dish: Dish;
  compact?: boolean;
  onOrder?: (dish: Dish) => void;
  onCook?: (dish: Dish) => void;
  onReview?: (dish: Dish) => void;
  onDelete?: (dish: Dish) => void;
}) {
  return (
    <article className={compact ? "dish-card compact" : "dish-card"}>
      <div className="dish-photo">
        {dish.imageUrl ? <img alt={dish.name} src={dish.imageUrl} /> : <Soup size={34} />}
      </div>
      <div className="dish-body">
        <div>
          <span className="pill">{dish.category}</span>
          <h3>{dish.name}</h3>
        </div>
        {dish.note && <p>{dish.note}</p>}
        <div className="meta-row">
          <Stars value={Math.round(dish.avgRating || dish.spouseRating || 0)} />
          <span>{dish.reviewCount ? `${dish.avgRating.toFixed(1)} 分` : "还没评分"}</span>
          <span>{dish.cookedCount} 次</span>
        </div>
        <div className="tag-row">
          {dish.tags.slice(0, 3).map((tag) => (
            <span key={tag}>#{tag}</span>
          ))}
        </div>
        {(onOrder || onCook || onReview || onDelete) && (
          <div className="button-row">
            {onOrder && (
              <button className="icon-button" onClick={() => onOrder(dish)} type="button">
                <ClipboardList size={18} />
                点这道
              </button>
            )}
            {onCook && (
              <button className="ghost-button" onClick={() => onCook(dish)} type="button">
                <ChefHat size={18} />
                做过
              </button>
            )}
            {onReview && (
              <button className="ghost-button" onClick={() => onReview(dish)} type="button">
                <Star size={18} />
                评分
              </button>
            )}
            {onDelete && (
              <button className="danger-button" onClick={() => onDelete(dish)} type="button">
                删除
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function AddDishForm({
  categories,
  session,
  onCreated
}: {
  categories: Category[];
  session: Session;
  onCreated: () => void;
}) {
  const [rating, setRating] = useState(5);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const categoryOptions = categories.length ? categories : fallbackCategories;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!navigator.onLine) {
      setMessage("离线时不能新增菜品，联网后再试。");
      return;
    }

    setLoading(true);
    setMessage("");
    const form = new FormData(formElement);
    form.set("spouseRating", String(rating));
    const photo = form.get("photo");
    try {
      if (photo instanceof File && photo.size > 0) {
        form.set("photo", await compressImage(photo));
      }
      await createDish(session, form);
      formElement.reset();
      setRating(5);
      setMessage("菜品已收录。");
      onCreated();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "新增失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="panel form-grid" onSubmit={submit}>
      <label>
        菜名
        <input name="name" placeholder="例如：番茄牛腩" required />
      </label>
      <label>
        分类
        <select name="category" defaultValue={categoryOptions[0]?.name || "家常菜"}>
          {categoryOptions.map((category) => (
            <option key={category.id} value={category.name}>
              {category.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        标签
        <input name="tags" placeholder="下饭, 孩子爱吃, 不辣" />
      </label>
      <label>
        照片
        <input accept="image/*" capture="environment" name="photo" type="file" />
      </label>
      <div className="field-block">
        <span>老婆初评</span>
        <Stars onChange={setRating} value={rating} />
      </div>
      <label>
        备注
        <textarea name="note" placeholder="做法要点、口味偏好、下次改进..." rows={4} />
      </label>
      {message && <p className={message.includes("失败") || message.includes("不能") ? "error-text" : "success-text"}>{message}</p>}
      <button className="primary-action" disabled={loading} type="submit">
        <Camera size={18} />
        {loading ? "保存中..." : "保存菜品"}
      </button>
    </form>
  );
}

function CategoryManager({
  categories,
  session,
  onChanged
}: {
  categories: Category[];
  session: Session;
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await addCategory(session, name.trim());
      setName("");
      setMessage("分类已新增。");
      onChanged();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "新增分类失败");
    } finally {
      setLoading(false);
    }
  }

  async function remove(category: Category) {
    if (!window.confirm(`确定删除「${category.name}」分类吗？已有菜品不会被删除。`)) return;
    setLoading(true);
    setMessage("");
    try {
      await deleteCategory(session, category.id);
      setMessage("分类已删除。");
      onChanged();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "删除分类失败");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="category-manager">
      <h3>分类管理</h3>
      <form className="inline-form" onSubmit={submit}>
        <input onChange={(event) => setName(event.target.value)} placeholder="例如：川菜" required value={name} />
        <button className="primary-action" disabled={loading} type="submit">
          <Plus size={18} />
          添加
        </button>
      </form>
      {message && <p className={message.includes("失败") || message.includes("存在") ? "error-text" : "success-text"}>{message}</p>}
      <div className="category-list">
        {categories.map((category) => (
          <span className="category-chip" key={category.id}>
            {category.name}
            <button disabled={loading} onClick={() => remove(category)} title={`删除 ${category.name}`} type="button">
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function ReviewModal({
  dish,
  session,
  onClose,
  onSaved
}: {
  dish: Dish | null;
  session: Session;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  if (!dish) return null;
  const activeDish = dish;

  async function save() {
    setLoading(true);
    try {
      await addReview(session, { dishId: activeDish.id, rating, comment });
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-panel">
        <h2>给「{activeDish.name}」评分</h2>
        <Stars onChange={setRating} value={rating} />
        <textarea
          onChange={(event) => setComment(event.target.value)}
          placeholder="今天味道怎么样？"
          rows={4}
          value={comment}
        />
        <div className="button-row">
          <button className="ghost-button" onClick={onClose} type="button">
            取消
          </button>
          <button className="primary-action" disabled={loading} onClick={save} type="button">
            {loading ? "保存中..." : "提交评分"}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [tab, setTab] = useState<Tab>("order");
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filters, setFilters] = useState<DishFilters>({ sort: "recent" });
  const [query, setQuery] = useState("");
  const [reviewDish, setReviewDish] = useState<Dish | null>(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  async function refresh() {
    if (!session) return;
    setLoading(true);
    try {
      const [{ dishes: nextDishes }, nextStats] = await Promise.all([
        getDishes(session, filters),
        getStats(session),
        getCategories(session).then((result) => setCategories(result.categories))
      ]);
      setDishes(nextDishes);
      setStats(nextStats);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [session, filters.category, filters.tag, filters.minRating, filters.sort]);

  const availableTags = useMemo(() => {
    return Array.from(new Set(dishes.flatMap((dish) => dish.tags))).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [dishes]);

  const visibleDishes = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return dishes;
    return dishes.filter((dish) => {
      const text = [dish.name, dish.category, dish.note, ...dish.tags.map((tag) => `#${tag}`), ...dish.tags]
        .join(" ")
        .toLowerCase();
      return text.includes(keyword);
    });
  }, [dishes, query]);

  if (!session) {
    return <AuthScreen onLogin={setSession} />;
  }
  const activeSession = session;

  async function handleOrder(dish: Dish) {
    if (!navigator.onLine) {
      setNotice("离线时不能点菜，联网后再试。");
      return;
    }
    await orderMeal(activeSession, dish.id, "ordered");
    setNotice(`已点「${dish.name}」。`);
    refresh();
  }

  async function handleCook(dish: Dish) {
    if (!navigator.onLine) {
      setNotice("离线时不能记录做过，联网后再试。");
      return;
    }
    await orderMeal(activeSession, dish.id, "cooked");
    setNotice(`已记录「${dish.name}」做过一次。`);
    refresh();
  }

  async function handleDelete(dish: Dish) {
    if (!window.confirm(`确定删除「${dish.name}」吗？历史评分会保留，但菜库不再显示。`)) return;
    await deleteDish(activeSession, dish.id);
    setNotice(`已删除「${dish.name}」。`);
    refresh();
  }

  function signOut() {
    clearSession();
    setSession(null);
  }

  const navItems: Array<{ tab: Tab; label: string; icon: typeof ClipboardList }> = [
    { tab: "order", label: "点菜", icon: ClipboardList },
    { tab: "library", label: "菜库", icon: Soup },
    { tab: "add", label: "新增", icon: Plus },
    { tab: "reviews", label: "评分", icon: Star },
    { tab: "stats", label: "榜单", icon: Trophy },
    { tab: "family", label: "家庭", icon: UsersRound }
  ];

  return (
    <div className="app-shell">
      <header className="top-bar">
        <div>
          <p>{session.householdName}</p>
          <h1>今天吃什么</h1>
        </div>
        <button className="round-button" onClick={signOut} title="退出登录" type="button">
          <LogOut size={20} />
        </button>
      </header>

      <main className="content-area">
        {notice && (
          <button className="notice" onClick={() => setNotice("")} type="button">
            {notice}
          </button>
        )}

        {(tab === "order" || tab === "library") && (
          <>
            <section className="tool-strip">
              <label className="search-box">
                <Search size={18} />
                <input onChange={(event) => setQuery(event.target.value)} placeholder="搜菜名、标签、备注" value={query} />
              </label>
              <select
                onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value || undefined }))}
                value={filters.category || ""}
              >
                <option value="">全部分类</option>
                {(categories.length ? categories : fallbackCategories).map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                onChange={(event) => setFilters((current) => ({ ...current, tag: event.target.value || undefined }))}
                value={filters.tag || ""}
              >
                <option value="">全部标签</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>
                    #{tag}
                  </option>
                ))}
              </select>
              <select
                onChange={(event) => setFilters((current) => ({ ...current, sort: event.target.value as DishFilters["sort"] }))}
                value={filters.sort}
              >
                <option value="recent">最近吃过</option>
                <option value="rating">评分最高</option>
                <option value="popular">做过最多</option>
              </select>
              <select
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    minRating: event.target.value ? Number(event.target.value) : undefined
                  }))
                }
                value={filters.minRating || ""}
              >
                <option value="">全部评分</option>
                <option value="5">5 星</option>
                <option value="4">4 星以上</option>
                <option value="3">3 星以上</option>
              </select>
            </section>

            {loading ? <p className="empty-state">菜单加载中...</p> : null}
            {!loading && visibleDishes.length === 0 ? <p className="empty-state">还没有菜，先新增一道拿手菜。</p> : null}
            <section className="dish-grid">
              {visibleDishes.map((dish) => (
                <DishCard
                  dish={dish}
                  key={dish.id}
                  onDelete={activeSession.role === "admin" && tab === "library" ? handleDelete : undefined}
                  onCook={handleCook}
                  onOrder={handleOrder}
                  onReview={setReviewDish}
                />
              ))}
            </section>
          </>
        )}

        {tab === "add" && <AddDishForm categories={categories} onCreated={refresh} session={activeSession} />}

        {tab === "reviews" && (
          <section className="panel stack">
            <h2>最近评分</h2>
            {stats?.recentReviews.length ? (
              stats.recentReviews.map((review) => (
                <article className="list-item" key={review.id}>
                  <div>
                    <strong>{review.dishName}</strong>
                    <p>{review.memberName}：{review.comment || "没有写短评"}</p>
                  </div>
                  <Stars value={review.rating} />
                </article>
              ))
            ) : (
              <p className="empty-state">还没有评分。</p>
            )}
          </section>
        )}

        {tab === "stats" && (
          <div className="stack">
            <section className="panel">
              <h2>最高分</h2>
              <div className="dish-grid compact-grid">
                {(stats?.topRated || []).map((dish) => (
                  <DishCard compact dish={dish} key={dish.id} />
                ))}
              </div>
            </section>
            <section className="panel">
              <h2>常点菜</h2>
              <div className="dish-grid compact-grid">
                {(stats?.mostCooked || []).map((dish) => (
                  <DishCard compact dish={dish} key={dish.id} />
                ))}
              </div>
            </section>
            <section className="panel">
              <h2>最想复做</h2>
              <div className="dish-grid compact-grid">
                {(stats?.wishAgain || []).map((dish) => (
                  <DishCard compact dish={dish} key={dish.id} />
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "family" && (
          <section className="panel stack">
            <h2>家庭成员</h2>
            <div className="invite-box">
              <Share2 size={20} />
              <div>
                <span>邀请码</span>
                <strong>{session.inviteCode || "创建者重新登录后可查看"}</strong>
              </div>
            </div>
            {stats?.members.map((member) => (
              <article className="list-item" key={member.id}>
                <div>
                  <strong>{member.nickname}</strong>
                  <p>{member.role === "admin" ? "管理员" : "成员"}</p>
                </div>
              </article>
            ))}
            {activeSession.role === "admin" && (
              <CategoryManager categories={categories} onChanged={refresh} session={activeSession} />
            )}
          </section>
        )}
      </main>

      <nav className="bottom-nav" aria-label="主导航">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button className={tab === item.tab ? "is-active" : ""} key={item.tab} onClick={() => setTab(item.tab)} type="button">
              <Icon size={20} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <ReviewModal dish={reviewDish} onClose={() => setReviewDish(null)} onSaved={refresh} session={activeSession} />
    </div>
  );
}

export default App;
